import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Activity,
  ArrowRight,
} from 'lucide-react';
import { Card } from '@components/common/Card';
import { StatCard } from '@components/common/StatCard';
import { Badge } from '@components/common/Badge';
import { Button } from '@components/common/Button';
import { getDashboardStats, getRecentActivity, getChartData } from '@mocks/admin';
import type { RootState } from '@store/index';

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const stats = getDashboardStats();
  const recentActivity = getRecentActivity(6);
  const chartData = getChartData();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'application':
        return <FileText className="h-4 w-4" />;
      case 'booking':
        return <Calendar className="h-4 w-4" />;
      case 'approval':
        return <CheckCircle className="h-4 w-4" />;
      case 'document':
        return <FileText className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'success':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'error':
        return 'text-red-500';
      default:
        return 'text-blue-500';
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-ncaa-dark">
          Welcome back, {user?.first_name}!
        </h1>
        <p className="text-ncaa-medium mt-2">Here's what's happening with your system today</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Applicants"
          value={stats.total_applicants.toLocaleString()}
          icon={<Users className="h-6 w-6" />}
          variant="primary"
          trend="+12% from last month"
          trendUp
          onClick={() => navigate('/admin/applicants')}
        />
        <StatCard
          title="Active Applications"
          value={stats.active_applications}
          icon={<FileText className="h-6 w-6" />}
          variant="info"
          action="View All"
          onClick={() => navigate('/admin/enrolments')}
        />
        <StatCard
          title="Pending Reviews"
          value={stats.pending_reviews}
          icon={<Clock className="h-6 w-6" />}
          variant="warning"
          action="Review Now"
          onClick={() => navigate('/admin/enrolments')}
        />
        <StatCard
          title="Approved Today"
          value={stats.approved_today}
          icon={<CheckCircle className="h-6 w-6" />}
          variant="success"
          trend="+3 from yesterday"
          trendUp
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          {/* Applications Chart */}
          <Card title="Application Trends" icon={<TrendingUp className="h-5 w-5" />}>
            <div className="space-y-4">
              {/* Simple Bar Chart */}
              <div className="grid grid-cols-7 gap-2">
                {chartData.applications_by_month.map((data, idx) => {
                  const maxValue = Math.max(
                    ...chartData.applications_by_month.map((d) => d.submitted)
                  );
                  const height = (data.submitted / maxValue) * 200;

                  return (
                    <div key={idx} className="flex flex-col items-center">
                      <div className="w-full flex items-end gap-0.5" style={{ height: '200px' }}>
                        <div
                          className="w-full bg-ncaa-primary rounded-t transition-all hover:opacity-80"
                          style={{ height: `${height}px` }}
                          title={`${data.month}: ${data.submitted} submitted`}
                        />
                        <div
                          className="w-full bg-green-500 rounded-t transition-all hover:opacity-80"
                          style={{ height: `${(data.approved / maxValue) * 200}px` }}
                          title={`${data.month}: ${data.approved} approved`}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-2 font-medium">{data.month}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-ncaa-primary rounded" />
                  <span className="text-gray-600">Submitted</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded" />
                  <span className="text-gray-600">Approved</span>
                </div>
              </div>
            </div>
          </Card>

          {/* Application Types Breakdown */}
          <Card title="Application Types" icon={<FileText className="h-5 w-5" />}>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.new_applications}</p>
                    <p className="text-xs text-gray-600">New Licenses</p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-blue-300">
                  <p className="text-xs text-blue-800">
                    {((stats.new_applications / stats.active_applications) * 100).toFixed(0)}% of active
                  </p>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.renewal_applications}</p>
                    <p className="text-xs text-gray-600">Renewals</p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-yellow-300">
                  <p className="text-xs text-yellow-800">
                    {((stats.renewal_applications / stats.active_applications) * 100).toFixed(0)}% of active
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats.total_bookings.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-4"
                onClick={() => navigate('/admin/bookings')}
              >
                Manage Bookings
              </Button>
            </Card>

            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Active Licenses</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {stats.active_licenses.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Currently valid</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="w-full mt-4"
                onClick={() => navigate('/admin/applicants')}
              >
                View Licenses
              </Button>
            </Card>
          </div>
        </div>

        {/* Sidebar - 1 column */}
        <div className="space-y-6">
          {/* System Health */}
          <Card title="System Health" icon={<Activity className="h-5 w-5" />}>
            <div className="text-center py-4">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-3 ${
                  stats.system_health === 'excellent'
                    ? 'bg-green-100'
                    : stats.system_health === 'good'
                    ? 'bg-blue-100'
                    : 'bg-yellow-100'
                }`}
              >
                <Activity
                  className={`h-10 w-10 ${
                    stats.system_health === 'excellent'
                      ? 'text-green-600'
                      : stats.system_health === 'good'
                      ? 'text-blue-600'
                      : 'text-yellow-600'
                  }`}
                />
              </div>
              <p className="text-lg font-bold text-gray-900 capitalize">{stats.system_health}</p>
              <p className="text-sm text-gray-600 mt-1">All systems operational</p>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-gray-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.todays_appointments}</p>
                <p className="text-xs text-gray-600 mt-1">Today's Tests</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{stats.pending_reviews}</p>
                <p className="text-xs text-gray-600 mt-1">Need Review</p>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card title="Quick Actions" icon={<ArrowRight className="h-5 w-5" />}>
            <div className="space-y-2">
              <Button
                variant="outline"
                fullWidth
                size="sm"
                onClick={() => navigate('/admin/enrolments')}
                icon={<FileText className="h-4 w-4" />}
              >
                Review Applications
              </Button>
              <Button
                variant="outline"
                fullWidth
                size="sm"
                onClick={() => navigate('/admin/centers')}
                icon={<Calendar className="h-4 w-4" />}
              >
                Manage Centers
              </Button>
              <Button
                variant="outline"
                fullWidth
                size="sm"
                onClick={() => navigate('/admin/users')}
                icon={<Users className="h-4 w-4" />}
              >
                User Management
              </Button>
            </div>
          </Card>

          {/* Recent Activity */}
          <Card title="Recent Activity" icon={<Clock className="h-5 w-5" />}>
            <div className="space-y-3">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex gap-3 pb-3 border-b border-gray-100 last:border-0">
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getStatusColor(
                      activity.status
                    )} bg-opacity-10`}
                  >
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
            <Button
              size="sm"
              variant="outline"
              fullWidth
              className="mt-4"
              onClick={() => navigate('/admin/audit')}
              icon={<ArrowRight className="h-4 w-4" />}
            >
              View All Activity
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
};
